import { Component, TemplateFunction } from '@ribajs/core'
import { hasChildNodesTrim } from '@ribajs/utils'
import { SbPopupComponent } from './sb-popup.component.js'

import type {
  ShopifyProductVariant,
  ShopifyProduct,
  ShopifyProductVariantOption,
  SbProductVariantImageDetails,
  Multipack,
} from '../types/index.js'

/**
 * The types for the scope
 */
interface Scope {
  // OPTIONS / DATA

  /** The shopify product data */
  product?: ShopifyProduct
  /** The current selected variant with data */
  variant?: ShopifyProductVariant
  /** The product options, see {@link SbProductComponent.prepareOptions} */
  options: ShopifyProductVariantOption[]

  /** The selected multipack */
  multipack?: Multipack

  images: SbProductVariantImageDetails[]

  activeImage?: SbProductVariantImageDetails

  popupTargetId: string

  // METHODS
  onClickThumb: SbProductVariantsGalleryComponent['onClickThumb']
  showPopup: SbProductVariantsGalleryComponent['showPopup']
  hidePopup: SbProductVariantsGalleryComponent['hidePopup']
  togglePopup: SbProductVariantsGalleryComponent['togglePopup']
  onThumbLoaded: SbProductVariantsGalleryComponent['onThumbLoaded']
  onImageLoaded: SbProductVariantsGalleryComponent['onImageLoaded']

  // TEMPLATES
}

/**
 * A component to show the product (variant) images on PDP
 */
export class SbProductVariantsGalleryComponent extends Component {
  public static tagName = 'sb-product-variants-gallery'

  protected popup?: SbPopupComponent

  public scope: Scope = {
    // OPTIONS / DATA
    product: undefined,
    variant: undefined,
    options: [],
    images: [],
    activeImage: undefined,
    multipack: undefined,
    popupTargetId: 'sb-popup-product-variants-gallery',

    // METHODS
    onClickThumb: this.onClickThumb.bind(this),
    showPopup: this.showPopup.bind(this),
    hidePopup: this.hidePopup.bind(this),
    togglePopup: this.togglePopup.bind(this),
    onThumbLoaded: this.onThumbLoaded.bind(this),
    onImageLoaded: this.onImageLoaded.bind(this),

    // TEMPLATES
  }

  static get observedAttributes(): string[] {
    return ['product', 'variant', 'options', 'multipack']
  }

  protected requiredAttributes(): string[] {
    return ['product', 'variant']
  }

  protected parsedAttributeChangedCallback(
    attributeName: string,
    oldValue: any,
    newValue: any,
    namespace: string | null,
  ) {
    super.parsedAttributeChangedCallback(
      attributeName,
      oldValue,
      newValue,
      namespace,
    )
    switch (attributeName) {
      case 'product':
      case 'variant':
      case 'multipack':
        this.refreshImages()
        break
    }
  }

  protected initPopup() {
    this.popup =
      document.querySelector(`sb-popup[id='${this.scope.popupTargetId}']`) ||
      undefined
    if (!this.popup) {
      throw new Error(
        `sb-popup element with id "${this.scope.popupTargetId}" not found!`,
      )
    }
  }

  public onClickThumb(image: SbProductVariantImageDetails) {
    this.setActiveImage(image)
  }

  protected setActiveImage(image?: SbProductVariantImageDetails) {
    if (image === this.scope.activeImage) {
      return
    }
    this.scope.activeImage = image
  }

  /**
   * Called as soon as this custom element es detected in the dom
   */
  protected connectedCallback() {
    super.connectedCallback()
    this.init(SbProductVariantsGalleryComponent.observedAttributes)
  }

  protected refreshImages() {
    if (!this.scope.variant || !this.scope.product?.sbImagesDetails) {
      this.scope.images = []
      this.scope.activeImage = undefined
      return
    }

    const images = (
      this.scope.product.sbImagesDetails[this.scope.variant.id] || []
    ).filter(
      image =>
        !image.multipack ||
        image.multipack === (this.scope.multipack?.quantity || 1),
    )

    this.scope.images = images

    const newActiveImage =
      this.scope.activeImage &&
      this.scope.images.includes(this.scope.activeImage)
        ? this.scope.activeImage
        : this.scope.images[0]

    this.setActiveImage(newActiveImage)
  }

  /**
   * Called after this custom element is bound and ready
   */
  protected async afterBind() {
    await super.afterBind()
    this.initPopup()
    this.classList.add(`${SbProductVariantsGalleryComponent.tagName}-ready`)
  }

  /**
   * Returns the template, if null is returned, the child html is not replaced, otherwise the innerHTML is replaced
   */
  protected template(): ReturnType<TemplateFunction> {
    // Use this template if this custom element has no child html
    if (!hasChildNodesTrim(this)) {
      return (
        <p class="error">
          This component has no it's own template, you need to add your own
          child HTML
        </p>
      )
    }
    return null
  }

  public onThumbLoaded(
    image: SbProductVariantImageDetails,
    eventName: 'changed' | 'loaded',
  ) {
    image.loadedThumbnail = eventName === 'loaded'
  }

  public onImageLoaded(
    image: SbProductVariantImageDetails | undefined,
    eventName: 'changed' | 'loaded',
  ) {
    if (!image) {
      return
    }

    image.loadedPreview = eventName === 'loaded'
  }

  public hidePopup(event?: Event) {
    if (!this.popup) {
      this.error(`Popup with id "${this.scope.popupTargetId}" not found!`)
      return
    }
    event?.preventDefault()
    event?.stopPropagation()
    this.popup.hide(event)
  }

  public showPopup(event?: Event) {
    if (!this.popup) {
      this.error(`Popup with id "${this.scope.popupTargetId}" not found!`)
      return
    }
    event?.preventDefault()
    event?.stopPropagation()
    this.popup.show(event, this.scope.images, true)
  }

  public togglePopup(event?: Event) {
    if (!this.popup) {
      this.error(`Popup with id "${this.scope.popupTargetId}" not found!`)
      return
    }
    event?.preventDefault()
    event?.stopPropagation()
    this.popup.toggle(event, this.scope.images, true)
  }
}
